// Design tokens first: the custom properties, the minimal reset and the base type rule become
// available app-wide. Single source of truth, shared verbatim with the design mockups
// (docs/design/spec/01-foundations.md).
//
// The legacy cascade that used to sit here — Bootstrap's CSS, `assets/css/{app,style,modern-theme}.css`
// and three self-hosted font families (Roboto, HelveticaNeue, Font Awesome) — is gone with the
// screens that needed it. Between them they were ~16,600 lines of which under 10% was still
// reachable, and they were a live source of bugs: bare `label` / `h5` / `thead` rules leaked into
// every antd control (see the fences that used to live in theme/antd.scss).
import "../docs/design/tokens.css";

// The few antd appearance rules its theme cannot express. Against antd's own later-injected
// <style> only specificity decides. See the file header.
import "./theme/antd.scss";

// Shared list-page chrome (spec/03 archetype A). Imported ONCE here rather than `@use`d from each
// list page's stylesheet: `@use` re-emits the module into every entry bundle, so a second copy
// landed after the page's own rules and beat them on source order at equal specificity — the
// visits bulk bar's green Approve reverted to the generic inverted button. One copy, and every
// page stylesheet is guaranteed to come after it.
import "./component/list-page.scss";

// Shared detail-page frame (spec/03 archetype B) — head, form+rail grid, section card, dirty-only
// save bar. Same one-import rule and the same reason as list-page.scss above.
import "./component/detail-page.scss";

// Reject → corrective task: the modal and its photo annotator, shared by /photo-review and
// /answers. Extracted from `photo-review.scss` for exactly that reason; same one-import rule.
import "./component/reject-task-modal.scss";

// The photo report review modal (zoom stage, thumbnail strip, facts rail), shared by
// /photo-review and /visit/:id's reports section. Extracted from `photo-review.scss` for the same
// reason as the two above, and under the same one-import rule.
import "./component/review-modal.scss";

import React from "react";
import { createRoot } from "react-dom/client";
import { ConfigProvider } from "antd";
import { Provider } from "react-redux";
import { BrowserRouter } from "react-router-dom";
import { ToastContainer } from "/src/util/alert";

import ErrorBoundary from "./component/ErrorBoundary";
import { saveState } from "./util/redux/localStorage";
import { getStore } from "./util/redux/store";

import AppRouter from "./router";
import { antdTheme } from "./theme/antd";

const store = getStore();
store.subscribe(() => {
  const { me } = store.getState();
  saveState({ me: me });
});

// Last line of defence for async throws that never reach a React boundary
// (rejected promises, event-handler errors). No remote reporting configured — log only.
window.addEventListener("unhandledrejection", event => {
  console.error("Unhandled promise rejection:", event.reason);
});
window.addEventListener("error", event => {
  console.error("Uncaught error:", event.error || event.message);
});

const application = (
  <Provider store={store}>
    <ConfigProvider theme={antdTheme}>
      <div>
        <ToastContainer position="top-center" autoClose={5000} limit={3} />
        <ErrorBoundary>
          <BrowserRouter>
            <AppRouter />
          </BrowserRouter>
        </ErrorBoundary>
      </div>
    </ConfigProvider>
  </Provider>
);

const root = createRoot(document.getElementById("app"));
root.render(application);
